'use client';

import { useRouter } from '@/i18n/routing';

const loginUri = '/auth/login';

export function useFetchWithCookie(): (uri: string, init?: RequestInit | undefined) => Promise<Response | null> {
    const router = useRouter();

    return async (uri, init) => {

        if (uri.startsWith('/')) {
            uri = uri.substring(1);
        }

        try {
            const resp = await fetch(`/api/${uri}`, {
                ...init,
                credentials: 'include'
            });

            if (resp.status === 401) {
                router.push(loginUri);
            }

            return resp;
        } catch (err: any) {
            console.error(err);
        }

        return null;
    };
}

export enum ServiceEnum {
    Api = 'api',
    Story = 'story',
    AgentConnector = 'agents',
    TaskManager = 'task-manager',
    AssetManager = 'asset-manager',
    Pricing = 'pricing',
    Watchers = 'watchers',
    Tax = 'tax',
    Notifications = 'notifications'
}

export const ServiceConfig: Record<ServiceEnum, { pathPrefix: string }> = {
    [ServiceEnum.Api]: {
        pathPrefix: 'api'
    },
    [ServiceEnum.Story]: {
        pathPrefix: 'story'
    },
    [ServiceEnum.AgentConnector]: {
        pathPrefix: 'agents'
    },
    [ServiceEnum.TaskManager]: {
        pathPrefix: 'api/taskmanager'
    },
    [ServiceEnum.AssetManager]: {
        pathPrefix: 'api/assetmanager'
    },
    [ServiceEnum.Pricing]: {
        pathPrefix: 'api/pricing'
    },
    [ServiceEnum.Watchers]: {
        pathPrefix: 'api/watchers'
    },
    [ServiceEnum.Tax]: {
        pathPrefix: 'api/tax-module'
    },
    [ServiceEnum.Notifications]: {
        pathPrefix: 'api/notifications-module'
    }
};

export class UnauthorizedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'UnauthorizedError';
    }
}

export type fetchFunction = (uri: string, init?: RequestInit | undefined) => Promise<Response>;

const useFetchService = (prefix: typeof ServiceConfig[ServiceEnum]): fetchFunction => {
    const router = useRouter();

    return async (uri: string, init?: RequestInit | undefined): Promise<Response> => {

        if (uri.startsWith('/')) {
            uri = uri.substring(1);
        }

        const resp = await fetch(`/${prefix.pathPrefix}/${uri}`, {
            ...init,
            credentials: 'include'
        });

        if (resp.status === 401) {
            router.push(loginUri);
            throw new UnauthorizedError('Unauthorized');
        }

        return resp;
    };
};

export const useFetchFromApi = (): fetchFunction => {
    return useFetchService(ServiceConfig[ServiceEnum.Api]);
};

export const useFetchFromStory = (): fetchFunction => {
    return useFetchService(ServiceConfig[ServiceEnum.Story]);
};

export const useFetchFromTaskManager = (): fetchFunction => {
    return useFetchService(ServiceConfig[ServiceEnum.TaskManager]);
};

export const useFetchFromTax = (): fetchFunction => {
    return useFetchService(ServiceConfig[ServiceEnum.Tax]);
};

export const useFetchFromWatchers = (): fetchFunction => {
    return useFetchService(ServiceConfig[ServiceEnum.Watchers]);
};

export const fetchFromTaxApiClient = (uri: string, init?: RequestInit | undefined): Promise<Response | null> => {
    if (uri.startsWith('/')) {
        uri = uri.substring(1);
    }
    return fetch(`/${ServiceConfig[ServiceEnum.Tax].pathPrefix}/${uri}`, {
        ...init,
        credentials: 'include'
    });
};

export const fetchFromWatchersApiClient = (uri: string, init?: RequestInit | undefined): Promise<Response | null> => {
    if (uri.startsWith('/')) {
        uri = uri.substring(1);
    }

    return fetch(`/${ServiceConfig[ServiceEnum.Watchers].pathPrefix}/${uri}`, {
        ...init,
        credentials: 'include'
    });
};

export const fetchFromTaskManagerApiClient = (uri: string, init?: RequestInit | undefined): Promise<Response | null> => {
    if (uri.startsWith('/')) {
        uri = uri.substring(1);
    }
    return fetch(`/${ServiceConfig[ServiceEnum.TaskManager].pathPrefix}/${uri}`, {
        ...init,
        credentials: 'include'
    });
};

export const fetchFromAssetManagerApiClient = (uri: string, init?: RequestInit | undefined): Promise<Response | null> => {
    if (uri.startsWith('/')) {
        uri = uri.substring(1);
    }
    return fetch(`/${ServiceConfig[ServiceEnum.AssetManager].pathPrefix}/${uri}`, {
        ...init,
        credentials: 'include'
    });
};

export const fetchFromApiClient = (uri: string, init?: RequestInit | undefined): Promise<Response | null> => {
    if (uri.startsWith('/')) {
        uri = uri.substring(1);
    }
    return fetch(`/${ServiceConfig[ServiceEnum.Api].pathPrefix}/${uri}`, {
        ...init,
        credentials: 'include'
    });
};

export const fetchFromPricingApiClient = (uri: string, init?: RequestInit | undefined): Promise<Response | null> => {
    if (uri.startsWith('/')) {
        uri = uri.substring(1);
    }
    return fetch(`/${ServiceConfig[ServiceEnum.Pricing].pathPrefix}/${uri}`, {
        ...init,
        credentials: 'include'
    });
};

export const fetchFromNotificationsApiClient = (uri: string, init?: RequestInit | undefined): Promise<Response | null> => {
    if (uri.startsWith('/')) {
        uri = uri.substring(1);
    }
    return fetch(`/${ServiceConfig[ServiceEnum.Notifications].pathPrefix}/${uri}`, {
        ...init,
        credentials: 'include'
    });
};
